import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { T, t } from '@sm/intl';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Input } from '@wds/input';
import { Modal } from '@wds/modal';
import { Typography } from '@wds/typography';
import { trackAmplitudeEvent } from '../../../lib';
import { validateTheme, NO_ERRORS } from '../../../validators';
import ThemeGuidanceCard from '../ThemeGuidanceCard';
import { useGeneral, useThemes } from '../../../Context';
export const COPY = {
  header: {
    id: 'OpenEndedCoding.AddThemeModal.header',
    defaultMessage: 'Create theme',
    description: '[Type: Header][Vis: med] - Header for Create Theme Modal'
  },
  cancel: {
    id: 'OpenEndedCoding.AddThemeModal.cancel',
    defaultMessage: 'Cancel',
    description: '[Type: Header][Vis: med] - Create Theme Modal cancel button'
  },
  create: {
    id: 'OpenEndedCoding.AddThemeModal.create',
    defaultMessage: 'Save',
    description: '[Type: Header][Vis: med] - Create Theme Modal create button'
  },
  theme: {
    id: 'OpenEndedCoding.AddThemeModal.theme',
    defaultMessage: 'Theme name',
    description: '[Type: Header][Vis: med] - Create Theme Modal theme title'
  },
  placeholder: {
    id: 'OpenEndedCoding.AddThemeModal.placeholder',
    defaultMessage: 'Enter theme name here',
    description: '[Type: Header][Vis: med] - Create Theme Modal placeholder'
  }
};
const AddThemeModal = ({
  show,
  selectedThemeId,
  createdSource,
  onClose,
  onCreate
}) => {
  const {
    surveyId,
    questionId,
    isSolutions
  } = useGeneral();
  const {
    themes
  } = useThemes();
  const selectedTheme = themes.find(theme => theme.id === selectedThemeId);
  const originalTheme = selectedTheme ? selectedTheme.theme : '';
  const [theme, setTheme] = useState(originalTheme);
  const [error, setError] = useState(NO_ERRORS);
  const touched = useRef(false);
  const handleThemeChange = e => {
    const themeName = e.currentTarget.value;
    setTheme(themeName);
    if (touched.current) {
      setError(validateTheme({
        themes,
        themeName,
        originalTheme
      }));
    }
  };
  useEffect(() => {
    setTheme(selectedTheme ? selectedTheme.theme : '');
  }, [selectedTheme]);
  const handleClose = () => {
    onClose();
    setError(NO_ERRORS);
    setTheme('');
  };
  const onFormSubmit = e => {
    e.preventDefault();
    // catch any validation errors
    const validated = validateTheme({
      themes,
      themeName: theme,
      originalTheme
    });
    if (validated.error) {
      setError(validated);
      touched.current = true;
      return;
    }
    onCreate(theme);
    onClose();
    setTheme('');
    // tracking
    trackAmplitudeEvent({
      amplitudeEvent: 'open-ended coding created theme',
      data: {
        surveyId,
        questionId,
        pageName: isSolutions ? 'ctm' : 'analyze',
        createdSource
      }
    });
  };
  return _jsx("form", {
    onSubmit: onFormSubmit,
    children: _jsxs(Modal, {
      "data-testid": "AddThemeModal__OEC",
      show: show,
      onClose: handleClose,
      size: "md",
      children: [_jsx(Modal.Header, {
        header: t(COPY.header)
      }), _jsx(Modal.Body, {
        children: _jsxs(Box, {
          mb: 5,
          children: [_jsx(Input, Object.assign({
            ariaLabel: "Create a theme",
            stretched: true,
            label: t(COPY.theme),
            value: theme,
            placeholder: t(COPY.placeholder),
            onChange: handleThemeChange
          }, error.error && {
            color: 'warning',
            errorMessage: _jsx(Box, {
              children: error.messages.map(message => _jsx(Typography, {
                component: "p",
                variant: "bodySm",
                color: "warning",
                children: message
              }, message))
            })
          })), _jsx(ThemeGuidanceCard, {})]
        })
      }), _jsxs(Modal.Footer, {
        align: "right",
        children: [_jsx(Button, {
          variant: "ghost",
          color: "secondary",
          onClick: handleClose,
          children: _jsx(T, {
            desc: COPY.cancel
          })
        }), _jsx(Button, {
          type: "submit",
          disabled: error.error || theme === originalTheme,
          onClick: onFormSubmit,
          children: _jsx(T, {
            desc: COPY.create
          })
        })]
      })]
    })
  });
};
export default AddThemeModal;